import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { SearchInput } from '../../components/ui/Input';
import { ArrowLeft, Building2, Users, Calendar, Activity, Settings, Eye, MousePointer, Share2, Download, MoreHorizontal, Edit2, Trash2 } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
const projectData: Record<string, any> = {
  '1': {
    id: 1,
    name: 'The Riverside Collection',
    builder: 'Excel Homes',
    builderId: 1,
    status: 'Active',
    buyers: 124,
    createdDate: '2023-06-15',
    lastActivity: '2 hours ago',
    description: 'Luxury waterfront homes with modern amenities and stunning river views.',
    models: 3,
    activeOptions: 245,
    totalOptions: 312
  },
  '2': {
    id: 2,
    name: 'Coastal Living',
    builder: 'Schell Brothers',
    builderId: 2,
    status: 'Active',
    buyers: 234,
    createdDate: '2023-03-22',
    lastActivity: '1 hour ago',
    description: 'Beachside community featuring contemporary coastal architecture.',
    models: 4,
    activeOptions: 189,
    totalOptions: 256
  },
  '3': {
    id: 3,
    name: 'Summit Ridge',
    builder: 'Mattamy Homes',
    builderId: 3,
    status: 'Active',
    buyers: 312,
    createdDate: '2022-11-10',
    lastActivity: '30 mins ago',
    description: 'Mountain-view estate homes with premium finishes and spacious layouts.',
    models: 5,
    activeOptions: 298,
    totalOptions: 387
  }
};
const activityData = [{
  date: 'Mon',
  buyers: 18,
  sessions: 45
}, {
  date: 'Tue',
  buyers: 22,
  sessions: 52
}, {
  date: 'Wed',
  buyers: 15,
  sessions: 38
}, {
  date: 'Thu',
  buyers: 28,
  sessions: 67
}, {
  date: 'Fri',
  buyers: 19,
  sessions: 48
}, {
  date: 'Sat',
  buyers: 12,
  sessions: 29
}, {
  date: 'Sun',
  buyers: 10,
  sessions: 21
}];
const recentBuyers = [{
  id: 1,
  name: 'John Smith',
  email: 'john@example.com',
  model: 'The Oakwood',
  joined: '2 hours ago'
}, {
  id: 2,
  name: 'Sarah Johnson',
  email: 'sarah@example.com',
  model: 'The Maple',
  joined: '5 hours ago'
}, {
  id: 3,
  name: 'Michael Chen',
  email: 'michael@example.com',
  model: 'The Cedar',
  joined: '1 day ago'
}, {
  id: 4,
  name: 'Emily Rodriguez',
  email: 'emily@example.com',
  model: 'The Oakwood',
  joined: '1 day ago'
}];
export function ProjectDetailPage() {
  const {
    projectId
  } = useParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const project = projectData[projectId || '1'];
  if (!project) {
    return <div className="p-8 text-center" data-id="element-712">
        <p className="text-slate-500" data-id="element-713">Project not found</p>
        <Button onClick={() => navigate('/admin/projects')} className="mt-4" data-id="element-714">
          Back to Projects
        </Button>
      </div>;
  }
  const filteredBuyers = recentBuyers.filter(buyer => buyer.name.toLowerCase().includes(searchQuery.toLowerCase()) || buyer.email.toLowerCase().includes(searchQuery.toLowerCase()));
  return <div className="space-y-6 max-w-7xl mx-auto" data-id="element-715">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-716">
        <div className="flex items-center gap-4" data-id="element-717">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} leftIcon={<ArrowLeft className="h-4 w-4" data-id="element-719" />} data-id="element-718">
            Back
          </Button>
          <div data-id="element-720">
            <div className="flex items-center gap-3" data-id="element-721">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-722">
                {project.name}
              </h1>
              <Badge variant={project.status === 'Active' ? 'success' : 'neutral'} data-id="element-723">
                {project.status}
              </Badge>
            </div>
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500" data-id="element-724">
              <div className="flex items-center gap-1" data-id="element-725">
                <Building2 className="h-4 w-4" data-id="element-726" />
                {project.builder}
              </div>
              <div className="flex items-center gap-1" data-id="element-727">
                <Calendar className="h-4 w-4" data-id="element-728" />
                Created {new Date(project.createdDate).toLocaleDateString()}
              </div>
              <div className="flex items-center gap-1" data-id="element-729">
                <Activity className="h-4 w-4" data-id="element-730" />
                Last activity {project.lastActivity}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 relative" data-id="element-731">
          <Button variant="outline" onClick={() => navigate(`/options/models`)} data-id="element-732">
            Manage Options
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowOptionsMenu(!showOptionsMenu)} data-id="element-733">
            <MoreHorizontal className="h-4 w-4" data-id="element-734" />
          </Button>

          {/* Options Menu */}
          {showOptionsMenu && <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50" data-id="element-735">
              <button onClick={() => {
            setShowOptionsMenu(false);
            // Handle edit
          }} className="flex items-center gap-3 w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-736">
                <Edit2 className="h-4 w-4 text-slate-400" data-id="element-737" />
                Edit Project
              </button>
              <button onClick={() => {
            setShowOptionsMenu(false);
            // Handle delete
          }} className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors" data-id="element-738">
                <Trash2 className="h-4 w-4" data-id="element-739" />
                Delete Project
              </button>
            </div>}
        </div>
      </div>

      {/* Description */}
      {project.description && <Card data-id="element-740">
          <CardContent className="p-6" data-id="element-741">
            <p className="text-slate-600" data-id="element-742">{project.description}</p>
          </CardContent>
        </Card>}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-743">
        <Card data-id="element-744">
          <CardContent className="flex items-center p-6" data-id="element-745">
            <div className="p-3 bg-primary-50 rounded-lg" data-id="element-746">
              <Users className="h-6 w-6 text-primary-600" data-id="element-747" />
            </div>
            <div className="ml-4" data-id="element-748">
              <p className="text-sm font-medium text-slate-500" data-id="element-749">
                Active Buyers
              </p>
              <h3 className="text-2xl font-bold text-slate-900" data-id="element-750">
                {project.buyers}
              </h3>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-751">
          <CardContent className="flex items-center p-6" data-id="element-752">
            <div className="p-3 bg-slate-100 rounded-lg" data-id="element-753">
              <Building2 className="h-6 w-6 text-slate-600" data-id="element-754" />
            </div>
            <div className="ml-4" data-id="element-755">
              <p className="text-sm font-medium text-slate-500" data-id="element-756">Models</p>
              <h3 className="text-2xl font-bold text-slate-900" data-id="element-757">
                {project.models}
              </h3>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-758">
          <CardContent className="flex items-center p-6" data-id="element-759">
            <div className="p-3 bg-slate-100 rounded-lg" data-id="element-760">
              <Settings className="h-6 w-6 text-slate-600" data-id="element-761" />
            </div>
            <div className="ml-4" data-id="element-762">
              <p className="text-sm font-medium text-slate-500" data-id="element-763">
                Active Options
              </p>
              <h3 className="text-2xl font-bold text-slate-900" data-id="element-764">
                {project.activeOptions}/{project.totalOptions}
              </h3>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-765">
          <CardContent className="flex items-center p-6" data-id="element-766">
            <div className="p-3 bg-slate-100 rounded-lg" data-id="element-767">
              <Activity className="h-6 w-6 text-slate-600" data-id="element-768" />
            </div>
            <div className="ml-4" data-id="element-769">
              <p className="text-sm font-medium text-slate-500" data-id="element-770">Avg Session</p>
              <h3 className="text-2xl font-bold text-slate-900" data-id="element-771">18m</h3>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Activity Chart */}
      <Card data-id="element-772">
        <CardHeader data-id="element-773">
          <CardTitle data-id="element-774">Activity Overview (Last 7 Days)</CardTitle>
        </CardHeader>
        <CardContent data-id="element-775">
          <div className="h-[300px] w-full" data-id="element-776">
            <ResponsiveContainer width="100%" height="100%" data-id="element-777">
              <LineChart data={activityData} data-id="element-778">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-779" />
                <XAxis dataKey="date" tick={{
                fill: '#64748b',
                fontSize: 12
              }} data-id="element-780" />
                <YAxis tick={{
                fill: '#64748b',
                fontSize: 12
              }} data-id="element-781" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} data-id="element-782" />
                <Line type="monotone" dataKey="buyers" stroke="#06b6d4" strokeWidth={2} dot={{
                fill: '#06b6d4'
              }} name="New Buyers" data-id="element-783" />
                <Line type="monotone" dataKey="sessions" stroke="#94a3b8" strokeWidth={2} dot={{
                fill: '#94a3b8'
              }} name="Sessions" data-id="element-784" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Recent Buyers */}
      <Card noPadding data-id="element-785">
        <CardHeader className="border-b border-slate-200 bg-slate-50" data-id="element-786">
          <div className="flex items-center justify-between" data-id="element-787">
            <CardTitle data-id="element-788">Recent Buyers</CardTitle>
            <SearchInput className="max-w-xs bg-white" placeholder="Search buyers..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-789" />
          </div>
        </CardHeader>

        <div className="overflow-x-auto" data-id="element-790">
          <table className="w-full text-left text-sm" data-id="element-791">
            <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-792">
              <tr data-id="element-793">
                <th className="px-6 py-4" data-id="element-794">Name</th>
                <th className="px-6 py-4" data-id="element-795">Email</th>
                <th className="px-6 py-4" data-id="element-796">Model Interest</th>
                <th className="px-6 py-4" data-id="element-797">Joined</th>
                <th className="px-6 py-4 text-right" data-id="element-798">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-799">
              {filteredBuyers.length === 0 ? <tr data-id="element-800">
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500" data-id="element-801">
                    No buyers found matching "{searchQuery}"
                  </td>
                </tr> : filteredBuyers.map(buyer => <tr key={buyer.id} className="hover:bg-slate-50 transition-colors" data-id="element-802">
                    <td className="px-6 py-4 font-medium text-slate-900" data-id="element-803">
                      {buyer.name}
                    </td>
                    <td className="px-6 py-4 text-slate-600" data-id="element-804">{buyer.email}</td>
                    <td className="px-6 py-4 text-slate-600" data-id="element-805">{buyer.model}</td>
                    <td className="px-6 py-4 text-slate-600" data-id="element-806">{buyer.joined}</td>
                    <td className="px-6 py-4 text-right" data-id="element-807">
                      <Button variant="ghost" size="sm" data-id="element-808">
                        View Details
                      </Button>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
}